import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, collection, query } from 'firebase/firestore';
import { db } from '../../services/firebase';

export default function FintechHeroWidget({ onOpenVault }) {
  const [vendorBalances, setVendorBalances] = useState({});
  const [integrations, setIntegrations] = useState({});
  const [networkTVL, setNetworkTVL] = useState(0);
  const [lowBalanceAlerts, setLowBalanceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Get the actual dollar balances from the system config
    const unsubBalances = onSnapshot(doc(db, 'system_config', 'vendor_wallets'), (snap) => {
      if (snap.exists()) setVendorBalances(snap.data());
    });

    // 2. Map the active integrations from the global settings
    const unsubConfig = onSnapshot(doc(db, 'system_config', 'global_settings'), (snap) => {
      if (snap.exists() && snap.data().active_integrations) {
        setIntegrations(snap.data().active_integrations);
      }
    });

    // 3. Network TVL calculation
    const clientsQuery = query(collection(db, 'clients'));
    const unsubClients = onSnapshot(clientsQuery, (snap) => {
      let total = 0;
      snap.forEach((docSnap) => {
        total += Number(docSnap.data().billing?.walletBalance || 0);
      });
      setNetworkTVL(total);
      setLoading(false);
    });

    return () => { unsubBalances(); unsubConfig(); unsubClients(); };
  }, []);

  // Isolate only pre-funded partners to show in Escrow. 
  // Fallback checks legacy keys so your treasury populates instantly without needing to edit the database.
  const preFundedVendors = Object.entries(integrations)
    .map(([key, data]) => ({ id: key, ...data }))
    .filter(v => v.billingType === 'PRE_FUNDED' || ['zendit', 'checkhq', 'easypost', 'sendgrid', 'openai', 'imei_info'].includes(v.id));
  
  useEffect(() => {
    const alerts = [];
    preFundedVendors.forEach(vendor => {
      const balance = Number(vendorBalances[vendor.id] || 0);
      if (balance < 50) alerts.push(vendor.name || vendor.id);
    });
    setLowBalanceAlerts(alerts);
  }, [vendorBalances, integrations]);

  const totalMasterEscrow = preFundedVendors.reduce((sum, vendor) => sum + Number(vendorBalances[vendor.id] || 0), 0);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm mb-8 relative">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        
        {/* Left: TVL */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-50 border border-emerald-100 rounded-sm flex items-center justify-center text-emerald-600 text-xl shadow-sm shrink-0">
            <i className="fa-solid fa-vault"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">eeKoPay Master Vault</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            </div>
            <h2 className="text-3xl font-black tracking-tight text-slate-900 leading-none">
              ${loading ? '---' : networkTVL.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-2 align-middle">Total Network TVL</span>
            </h2>
          </div>
        </div>

        {/* Center: Escrow Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 border-y lg:border-y-0 lg:border-x border-slate-100 py-4 lg:py-0 lg:px-8 w-full lg:w-auto">
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Master API Escrow</p>
            <p className="text-lg font-black text-slate-800">
              ${totalMasterEscrow.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Escrow Wallets</p>
            <p className="text-lg font-black text-slate-800">{preFundedVendors.length} Linked</p>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Gateway Status</p>
            {lowBalanceAlerts.length > 0 ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-black text-amber-600 uppercase tracking-wider bg-amber-50 px-2 py-1 rounded-sm border border-amber-200">
                <i className="fa-solid fa-triangle-exclamation"></i> {lowBalanceAlerts.length} Low Float
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-black text-emerald-600 uppercase tracking-wider bg-emerald-50 px-2 py-1 rounded-sm border border-emerald-200">
                <i className="fa-solid fa-shield-check"></i> Optimal Float
              </span>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="w-full lg:w-auto shrink-0">
          <button
            onClick={onOpenVault}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-6 py-3 rounded-sm shadow-sm transition-colors flex items-center justify-center gap-2"
          >
            <i className="fa-solid fa-cash-register"></i> Open Treasury Terminal
          </button>
        </div>
      </div>

      {lowBalanceAlerts.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-amber-700 font-bold">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-circle-exclamation"></i>
            <span>Action Required: Pre-Funded Escrow balance below safety threshold on: <strong>{lowBalanceAlerts.join(', ')}</strong></span>
          </div>
          <button onClick={onOpenVault} className="text-[10px] font-black uppercase tracking-widest underline hover:text-amber-600">
            Replenish Escrow &rarr;
          </button>
        </div>
      )}
    </div>
  );
}